// ============================================================
// Somatocarta.js — os três componentes num plano só
//
// O somatotipo são três números e ninguém lê três números no tempo. A
// somatocarta é a projeção que Heath e Carter publicaram justamente para
// isso: cada avaliação vira um ponto, a série vira um caminho, e a
// direção do caminho diz o que mudou na forma do corpo.
//
//   x = ectomorfia − endomorfia
//   y = 2 × mesomorfia − (endomorfia + ectomorfia)
//
// ── O desenho ───────────────────────────────────────────────
//
// Os três vértices são os extremos do método: endomorfo puro (7-1-1) em
// (−6, −6), mesomorfo puro (1-7-1) em (0, 12) e ectomorfo puro (1-1-7)
// em (6, −6). Os lados são curvos porque a projeção é curva: os
// somatotipos possíveis não formam um triângulo de lados retos.
//
// A silhueta em cada vértice existe para o atleta e para a família.
// "Mesomorfia 3,8" não diz nada para quem não é da área, e o boneco ao
// lado do vértice resolve isso sem precisar de legenda. As três são
// desenhadas aqui mesmo, a partir de três larguras de ombro, cintura e
// quadril, e não são foto de ninguém.
//
// ── O ponto médio ───────────────────────────────────────────
//
// O círculo vazado é a média dos pontos. Na série de um atleta ele é o
// centro da nuvem dele, e a distância de cada ponto ao centro separa
// mudança real de forma de oscilação de medida: Carter trata diferença
// acima de 2 unidades como mudança, e abaixo disso é a fita.
// ============================================================

import { AZUL, AZUL_ESCURO, VERDE_LOGO, CINZA_ESC, TEXTO, BORDA, BRANCO, TIPO } from "../tema";
import { laudoDoSomatotipo } from "../avaliacaoFisica";
import { Vazio } from "./Dados";

// ── A escala dos dois eixos é diferente, e tem de ser ───────
//
// Os vértices são (0, 12), (−6, −6) e (6, −6): o de cima está a 12
// unidades do centro e os de baixo a 8,5. Desenhado com a mesma escala
// nos dois eixos, o triângulo sai esticado para cima, parecendo uma
// folha, e foi assim que ele saiu na primeira versão disto.
//
// A carta publicada é equilátera, e para isso a escala vertical tem de
// ser tan(30°) da horizontal: aí os três vértices ficam à mesma
// distância do centro e a 120° um do outro, que é a geometria que o
// desenho promete.
const ESCALA_X = 20;
const ESCALA_Y = ESCALA_X * Math.tan(Math.PI / 6);
const LARGURA = 374;
const ALTURA_BASE = 330;
const CENTRO = { x: LARGURA / 2, y: 212 };
// A distância do centro a qualquer vértice, em pixels.
const RAIO = 12 * ESCALA_Y;

const VERTICES = [
  { id: "meso", x: 0, y: 12, rotulo: "Mesomorfia" },
  { id: "endo", x: -6, y: -6, rotulo: "Endomorfia" },
  { id: "ecto", x: 6, y: -6, rotulo: "Ectomorfia" },
];

// As três formas, em três medidas. O mesomorfo tem ombro largo e cintura
// estreita, o endomorfo tem o tronco de largura quase constante, o
// ectomorfo é estreito dos dois lados. É o bastante para a leitura de
// relance, que é o que a silhueta precisa entregar.
const FORMAS = {
  meso: { ombro: 15, cintura: 8.5, quadril: 10.5, braco: 3.4, perna: 4.4, cabeca: 3.6 },
  endo: { ombro: 11.5, cintura: 13, quadril: 13, braco: 3.6, perna: 5, cabeca: 3.7 },
  ecto: { ombro: 8.5, cintura: 6, quadril: 6.8, braco: 2, perna: 2.9, cabeca: 3.2 },
};

// Uma silhueta de pé, desenhada dentro de uma caixa de 44 por 64, com o
// pé na origem do grupo. Quem posiciona é quem chama.
function Silhueta({ tipo, cor }) {
  const f = FORMAS[tipo];
  const meio = 0;
  const yOmbro = -46;
  const yCintura = -30;
  const yQuadril = -22;

  const tronco = [
    [meio - f.ombro, yOmbro],
    [meio + f.ombro, yOmbro],
    [meio + f.cintura, yCintura],
    [meio + f.quadril, yQuadril],
    [meio - f.quadril, yQuadril],
    [meio - f.cintura, yCintura],
  ].map(([x, y]) => `${x},${y}`).join(" ");

  return (
    <g fill={cor} stroke="none">
      <circle cx={meio} cy={yOmbro - f.cabeca - 2.5} r={f.cabeca} />
      <polygon points={tronco} />
      {/* Braços, um de cada lado, levemente abertos */}
      {[-1, 1].map(lado => (
        <polygon key={lado} points={[
          [meio + lado * (f.ombro - 0.5), yOmbro + 1],
          [meio + lado * (f.ombro + f.braco), yOmbro + 3],
          [meio + lado * (f.cintura + f.braco + 1.5), yQuadril + 1],
          [meio + lado * (f.cintura + 1.5), yQuadril],
        ].map(([x, y]) => `${x},${y}`).join(" ")} />
      ))}
      {/* Pernas */}
      {[-1, 1].map(lado => (
        <polygon key={lado} points={[
          [meio + lado * 0.8, yQuadril],
          [meio + lado * (f.quadril - 0.5), yQuadril],
          [meio + lado * (f.perna + 1.5), -1],
          [meio + lado * 1, -1],
        ].map(([x, y]) => `${x},${y}`).join(" ")} />
      ))}
    </g>
  );
}

function emBR(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ""))) return String(iso || "");
  const [a, m, d] = iso.split("-");
  return `${d}/${m}/${a.slice(2)}`;
}

export default function Somatocarta({ pontos = [], medio = null, altura = ALTURA_BASE }) {
  const validos = pontos.filter(
    p => p && Number.isFinite(p.x) && Number.isFinite(p.y),
  );

  // O y do SVG cresce para baixo e o do somatotipo para cima: a
  // mesomorfia tem de ficar no alto, e é esta inversão que faz isso.
  const px = x => CENTRO.x + x * ESCALA_X;
  const py = y => CENTRO.y - y * ESCALA_Y;

  if (!validos.length) {
    return (
      <Vazio titulo="A somatocarta aparece quando uma avaliação tiver as dobras, os perímetros e os diâmetros do somatotipo." />
    );
  }

  // Os lados curvos, montados já em pixels.
  //
  // O ponto de controle de cada lado é o meio do lado afastado do centro
  // para o dobro da distância dele. Numa Bézier quadrática isso põe o
  // meio da curva a três quartos do raio, contra meio raio da corda
  // reta: é a barriga que a carta publicada tem.
  const curva = () => {
    const vs = VERTICES.map(v => ({ x: px(v.x), y: py(v.y) }));
    const partes = vs.map((v, i) => {
      const prox = vs[(i + 1) % vs.length];
      const mx = (v.x + prox.x) / 2;
      const my = (v.y + prox.y) / 2;
      const ctrl = {
        x: CENTRO.x + (mx - CENTRO.x) * 2,
        y: CENTRO.y + (my - CENTRO.y) * 2,
      };
      return `Q ${ctrl.x},${ctrl.y} ${prox.x},${prox.y}`;
    });
    return `M ${vs[0].x},${vs[0].y} ${partes.join(" ")} Z`;
  };

  const ultimo = validos[validos.length - 1];
  const laudo = laudoDoSomatotipo(ultimo);

  return (
    <div style={{ display: "grid", gap: 8, justifyItems: "center" }}>
      <svg viewBox={`0 0 ${LARGURA} ${ALTURA_BASE}`}
        style={{ width: "100%", maxWidth: 520, height: altura }}>
        <path d={curva()} fill={BORDA} fillOpacity="0.22" stroke={AZUL} strokeWidth="1.6" />

        {/* As três medianas, do centro a cada vértice, e as três
            bissetrizes tracejadas, que separam os seis setores de
            predominância. É o mesmo desenho da carta publicada. */}
        {VERTICES.map(v => (
          <line key={v.id} x1={CENTRO.x} y1={CENTRO.y} x2={px(v.x)} y2={py(v.y)}
            stroke={AZUL} strokeWidth="1" strokeOpacity="0.55" />
        ))}
        {/* As três bissetrizes, a 90, 210 e 330 graus de tela, que é onde
            elas ficam depois que a escala vertical deixou o triângulo
            equilátero. Elas separam os seis setores de predominância. */}
        {[90, 210, 330].map(graus => {
          const r = (graus * Math.PI) / 180;
          return (
            <line key={graus}
              x1={CENTRO.x} y1={CENTRO.y}
              x2={CENTRO.x + Math.cos(r) * RAIO * 0.92}
              y2={CENTRO.y + Math.sin(r) * RAIO * 0.92}
              stroke={AZUL} strokeWidth="1" strokeDasharray="5 4" strokeOpacity="0.5" />
          );
        })}

        {/* A silhueta de cada perfil, fora do vértice, e o nome junto */}
        {VERTICES.map(v => {
          // Fora do vértice, e longe o bastante para não cobrir o nome:
          // na primeira versão o boneco ficava em cima da palavra
          // "Endomorfia".
          const fora = v.id === "meso"
            ? { x: px(v.x), y: py(v.y) - 18 }
            : { x: px(v.x) + (v.id === "endo" ? -58 : 58), y: py(v.y) + 46 };
          return (
            <g key={v.id}>
              <g transform={`translate(${fora.x}, ${fora.y})`}>
                <Silhueta tipo={v.id} cor={AZUL_ESCURO} />
              </g>
              {/* O nome do vértice de baixo é ancorado para DENTRO do
                  triângulo. Centrado no vértice, ele avança para fora e
                  encosta na silhueta, que é onde ele estava antes. */}
              <text
                x={px(v.x) + (v.id === "meso" ? 0 : v.id === "endo" ? 7 : -7)}
                y={py(v.y) + (v.id === "meso" ? -4 : 16)}
                textAnchor={v.id === "meso" ? "middle" : v.id === "endo" ? "start" : "end"}
                fontSize="11.5" fontWeight="600" fill={TEXTO}
              >{v.rotulo}</text>
            </g>
          );
        })}

        {validos.length > 1 && (
          <polyline points={validos.map(p => `${px(p.x)},${py(p.y)}`).join(" ")}
            fill="none" stroke={AZUL} strokeWidth="1.6" strokeDasharray="4 3" />
        )}

        {/* A data fica só no primeiro e no último ponto, e para lados
            opostos. Duas avaliações de um atleta caem quase no mesmo
            lugar, porque mudar de forma é lento, e rotular todos os pontos
            empilha texto em cima de texto. Os do meio respondem no
            passar do mouse. */}
        {validos.map((p, i) => {
          const extremo = i === 0 || p === ultimo;
          return (
            <g key={p.data || i}>
              <circle cx={px(p.x)} cy={py(p.y)} r={p === ultimo ? 6 : 4}
                fill={p === ultimo ? AZUL : BRANCO} stroke={AZUL} strokeWidth="1.8">
                <title>
                  {`${emBR(p.data)}: ${p.endomorfia}-${p.mesomorfia}-${p.ectomorfia}`}
                </title>
              </circle>
              {p.data && extremo && (
                <text
                  x={px(p.x) + (i === 0 ? -9 : 9)}
                  y={py(p.y) + (i === 0 ? 15 : -8)}
                  textAnchor={i === 0 ? "end" : "start"}
                  fontSize="9.5" fill={CINZA_ESC}
                >{emBR(p.data)}</text>
              )}
            </g>
          );
        })}

        {medio && Number.isFinite(medio.x) && Number.isFinite(medio.y) && (
          <g>
            <circle cx={px(medio.x)} cy={py(medio.y)} r="7"
              fill="none" stroke={VERDE_LOGO} strokeWidth="2.2" />
            <circle cx={px(medio.x)} cy={py(medio.y)} r="1.6" fill={VERDE_LOGO} />
          </g>
        )}
      </svg>

      {/* O perfil, lido.
          A linha de números crus saiu em 15/10/2026, a pedido do Diego:
          "1.98-2.5-3.79" não diz nada a quem não é da área, e os três
          valores já estão no desenho e na tabela de resultados. No lugar
          dela entra a classificação e o que ela significa. */}
      {laudo && (
        <div style={{ textAlign: "center", maxWidth: 520 }}>
          <div style={{ ...TIPO.corpo, color: TEXTO, fontWeight: 700 }}>
            {laudo.classificacao}
          </div>
          <div style={{ ...TIPO.apoio, color: CINZA_ESC, marginTop: 2 }}>
            {laudo.texto}
          </div>
        </div>
      )}
      {/* A legenda é lida pelo atleta e pela família, e também pela
          comissão técnica. Por isso cada componente vem com o nome
          técnico e com o que ele é em palavra de todo dia, nessa ordem:
          quem já sabe lê o primeiro, quem não sabe lê o segundo. */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(156px, 1fr))",
        gap: 10, maxWidth: 520, width: "100%",
      }}>
        {[
          ["Endomorfia", "adiposidade relativa", "quanto do corpo é gordura"],
          ["Mesomorfia", "robustez músculo-esquelética", "quanto de músculo e de osso para a altura"],
          ["Ectomorfia", "linearidade", "quanto o corpo é longo e fino para o peso"],
        ].map(([nome, tecnico, simples]) => (
          <div key={nome} style={{ ...TIPO.apoio, textAlign: "center" }}>
            <strong style={{ color: TEXTO }}>{nome}</strong>
            <div style={{ color: TEXTO }}>{tecnico}</div>
            <div style={{ color: CINZA_ESC }}>{simples}</div>
          </div>
        ))}
      </div>
      <div style={{ ...TIPO.apoio, color: CINZA_ESC, textAlign: "center", maxWidth: 520 }}>
        Os três andam juntos e são sempre lidos juntos: o que a carta mostra é a forma do
        corpo, não o tamanho dele. Quanto mais perto de um vértice, mais aquele componente
        predomina; no centro, os três se equilibram. Avaliações do mesmo atleta formam um
        caminho, e é a direção dele que diz o que o treino e o crescimento mudaram.
        {laudo ? ` ${laudo.ressalva}` : ""}
      </div>
    </div>
  );
}
